import * as React from "react";
import { flushSync } from "react-dom";
import { useUsersManagementContext } from "./users-management-context.js";

interface SearchContextType {
  inputRef: React.RefObject<HTMLInputElement | null>;
  clearSearch: () => void;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
}

const SearchContext = React.createContext<SearchContextType | undefined>(
  undefined,
);
SearchContext.displayName = "SearchContext";

export const SearchProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const {
    state: { searchQuery },
    dispatch,
  } = useUsersManagementContext();

  const [searchValue, setSearchValue] = React.useState(searchQuery || "");

  const clearSearch = React.useCallback(() => {
    flushSync(() => {
      setSearchValue("");
      dispatch({ type: "FILTER_BY_SEARCH", searchQuery: "" });
    });
    inputRef.current?.focus();
  }, [dispatch]);

  return (
    <SearchContext.Provider
      value={{ inputRef, clearSearch, searchValue, setSearchValue }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => {
  const context = React.useContext(SearchContext);
  if (!context) {
    throw new Error("useSearchContext must be used within a SearchProvider");
  }
  return context;
};
